import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslator } from "@sia/style-guide";
import surveyAdapter from "common/Adapters/SurveyAdapter/Router";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { buildRequestOverheads, toApiAmount } from "./overheadsAutosave.model";
import { VALUE_HISTORY_SWITCH_REQUESTED, isCurrentPersistenceIntent } from "./overheadsAutosave.history";
import { createInitialOverheadsAutosaveState, overheadsAutosaveReducer } from "./overheadsAutosave.reducer";
import {
  selectBadge,
  selectDraftOverheads,
  selectFilledOutProgress,
  selectIsOverheadsComplete,
  selectParentChildrenTotalErrors,
} from "./overheadsAutosave.selectors";
import { classifySaveResult, reconcileAfterFailure } from "./overheadsPersistence";

const sortIntents = (left, right) => left.sequence - right.sequence;

export function useOverheads() {
  const translate = useTranslator();
  const { companyOverheads, companyInputId, setCollectionData } = useContext(DataCollectionContext);

  const [state, reactDispatch] = useReducer(
    overheadsAutosaveReducer,
    { companyInputId, overheads: companyOverheads || [] },
    createInitialOverheadsAutosaveState
  );

  const stateRef = useRef(state);
  const requestSequenceRef = useRef(0);
  const requestActiveRef = useRef(false);
  const mountedRef = useRef(true);
  const hydratedSessionRef = useRef(companyInputId ?? null);
  const [feedbackClock, setFeedbackClock] = useState(() => Date.now());

  const send = useCallback(
    (action) => {
      const nextState = overheadsAutosaveReducer(stateRef.current, action);
      stateRef.current = nextState;
      reactDispatch(action);
      return nextState;
    },
    [reactDispatch]
  );

  const publishConfirmedOverheads = useCallback(
    (overheads) => {
      setCollectionData((current) => ({
        ...current,
        companyOverheads: overheads,
      }));
    },
    [setCollectionData]
  );

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const nextSessionKey = companyInputId ?? null;
    if (nextSessionKey === hydratedSessionRef.current) return;
    hydratedSessionRef.current = nextSessionKey;
    send({
      type: "HYDRATE",
      companyInputId: nextSessionKey,
      overheads: Array.isArray(companyOverheads) ? companyOverheads : [],
    });
  }, [companyInputId, companyOverheads, send]);

  useEffect(() => {
    if (!state.lastSuccessfulSaveAt) return undefined;
    setFeedbackClock(Date.now());
    const timer = window.setTimeout(() => setFeedbackClock(Date.now()), 1500);
    return () => window.clearTimeout(timer);
  }, [state.lastSuccessfulSaveAt]);

  const completeReconciliation = useCallback(
    async ({ requestId, failure }) => {
      const reconciliation = await reconcileAfterFailure({ surveyAdapter, translate });
      if (!mountedRef.current) return false;
      if (stateRef.current.persistence.inFlight?.requestId !== requestId) return false;
      requestActiveRef.current = false;
      if (!reconciliation.ok) {
        send({ type: "RECONCILIATION_FAILED", requestId, message: reconciliation.message });
        return false;
      }
      send({ type: "RECONCILIATION_SUCCEEDED", requestId, serverOverheads: reconciliation.overheads, failure, completedAt: Date.now() });
      publishConfirmedOverheads(reconciliation.overheads);
      return true;
    },
    [publishConfirmedOverheads, send, translate]
  );

  const startNextRequest = useCallback(
    async (snapshot) => {
      if (requestActiveRef.current || snapshot.persistence.phase !== "idle") return;
      const pending = Object.values(snapshot.persistence.pendingByField).sort(sortIntents);
      if (pending.length === 0) return;
      const staleFieldKeys = [];
      const includedByField = {};
      pending.forEach((intent) => {
        const field = snapshot.fields[intent.fieldKey];
        const intentIsCurrent = isCurrentPersistenceIntent({
          intent,
          valueHistory: snapshot.valueHistory,
          field,
        });
        if (!intentIsCurrent) {
          staleFieldKeys.push(intent.fieldKey);
          return;
        }
        includedByField[intent.fieldKey] = {
          ...intent,
          submittedApiValue: toApiAmount(intent.draftValue),
          previousConfirmedValue: field.confirmedValue,
        };
      });
      if (staleFieldKeys.length > 0) send({ type: "DROP_STALE_PENDING", fieldKeys: staleFieldKeys });
      if (Object.keys(includedByField).length === 0) return;
      const requestId = ++requestSequenceRef.current;
      const requestSessionKey = snapshot.sessionKey;
      const startedState = send({ type: "REQUEST_STARTED", requestId, includedByField });
      const inFlight = startedState.persistence.inFlight;
      if (!inFlight || inFlight.requestId !== requestId) return;
      const payload = buildRequestOverheads(startedState, inFlight.includedByField);
      requestActiveRef.current = true;
      let result;
      try {
        result = await surveyAdapter.updateOverheadCosts(payload, requestSessionKey, translate);
      } catch (error) {
        result = { ok: false, data: undefined, error };
      }
      if (!mountedRef.current) {
        requestActiveRef.current = false;
        return;
      }
      if (stateRef.current.sessionKey !== requestSessionKey || stateRef.current.persistence.inFlight?.requestId !== requestId) {
        requestActiveRef.current = false;
        return;
      }
      const classification = classifySaveResult(result, startedState.fieldOrder);
      if (classification.kind === "success") {
        requestActiveRef.current = false;
        send({ type: "REQUEST_SUCCEEDED", requestId, serverOverheads: classification.overheads, completedAt: Date.now() });
        publishConfirmedOverheads(classification.overheads);
        return;
      }
      send({ type: "REQUEST_RECONCILING", requestId, error: classification });
      await completeReconciliation({ requestId, failure: classification });
    },
    [completeReconciliation, publishConfirmedOverheads, send, translate]
  );

  useEffect(() => {
    if (state.persistence.phase === "idle" && Object.keys(state.persistence.pendingByField).length > 0 && !requestActiveRef.current) {
      startNextRequest(state);
    }
  }, [startNextRequest, state]);

  const retryReconciliation = useCallback(async () => {
    const snapshot = stateRef.current;
    const inFlight = snapshot.persistence.inFlight;
    if (snapshot.persistence.phase !== "unverified" || !inFlight || requestActiveRef.current) return snapshot.persistence.phase !== "unverified";
    requestActiveRef.current = true;
    send({ type: "RECONCILIATION_RETRY_STARTED" });
    return completeReconciliation({
      requestId: inFlight.requestId,
      failure: snapshot.persistence.error,
    });
  }, [completeReconciliation, send]);

  const changeField = useCallback(
    (fieldKey, value) => {
      send({ type: "FIELD_CHANGED", fieldKey, value });
    },
    [send]
  );

  const blurField = useCallback(
    (fieldKey) => {
      send({ type: "FIELD_BLURRED", fieldKey });
      if (stateRef.current.persistence.phase === "unverified") retryReconciliation();
    },
    [retryReconciliation, send]
  );

  const activateFieldAction = useCallback(
    (fieldKey) => {
      const snapshot = stateRef.current;
      const field = snapshot.fields[fieldKey];
      if (!field || field.phase === "saving") return;
      const ownsValueHistory = snapshot.valueHistory?.ownerFieldKey === fieldKey && field.phase === "saved";
      send({ type: ownsValueHistory ? VALUE_HISTORY_SWITCH_REQUESTED : "FIELD_REVERTED", fieldKey });
    },
    [send]
  );

  const items = useMemo(() => selectDraftOverheads(state), [state]);
  const badge = useMemo(() => selectBadge(state, feedbackClock), [feedbackClock, state]);
  const progress = useMemo(() => selectFilledOutProgress(state), [state]);
  const parentChildrenTotalErrors = useMemo(() => selectParentChildrenTotalErrors(state), [state]);
  const isOverheadsComplete = useMemo(() => selectIsOverheadsComplete(state), [state]);

  return useMemo(
    () => ({
      items,
      fieldsByKey: state.fields,
      undoOwnerFieldKey: state.valueHistory?.ownerFieldKey || null,
      badge,
      feedbackClock,
      progress,
      parentChildrenTotalErrors,
      isOverheadsComplete,
      changeField,
      blurField,
      activateFieldAction,
    }),
    [
      activateFieldAction,
      badge,
      blurField,
      changeField,
      feedbackClock,
      isOverheadsComplete,
      items,
      parentChildrenTotalErrors,
      progress,
      state.fields,
      state.valueHistory,
    ]
  );
}
